import { palettes, tokens } from '@treams/treams-ui/theme/tokens';

export type TalentReviewCellColors = {
	/** Cell border. */
	border: string;
	/** Label text: the dark shade of the border's hue, so light borders keep readable text. */
	label: string;
	/** Faint tint of the same hue, used for the decorative swirl. */
	soft: string;
};

type Shade = keyof typeof palettes.vividPink;

/**
 * Bands of a single hue, one per border shade. The label takes a dark shade of
 * that same hue so a light border still carries readable text, and the swirl
 * takes its faintest tint.
 * @param palette the hue all three colours come from
 * @param labelShade dark shade used for the label text
 * @param borderShades border shades, light to dark; one band each
 */
function createCellColorVariants(
	palette: Record<Shade, string>,
	labelShade: Shade,
	borderShades: Shade[],
): TalentReviewCellColors[] {
	return borderShades.map((shade) => ({
		border: palette[shade],
		label: palette[labelShade],
		soft: palette['50'],
	}));
}

export function encodeCellColor(color: string): number {
	return parseInt(color.slice(1), 16);
}

export function decodeCellColor(color: number): string {
	return `#${color.toString(16).padStart(6, '0')}`;
}

/** Neutral colours for a past day: the grid is history, not something to work in. */
export const pastCellColors: TalentReviewCellColors = {
	border: tokens.border.default,
	label: tokens.text.secondary,
	soft: tokens.surface.secondary,
};

/** The viewer's own cell on a shared review: brand border, label, and wash. */
export const emphasizedCellColors: TalentReviewCellColors = {
	border: tokens.brand.border,
	label: tokens.brand.text,
	soft: tokens.brand.background,
};

// Default colours run along the anti-diagonals, from low/low (bottom left) to high/high (top
// right): peach, then blue, then pink. Several shades per hue, so a 5×5 grid — the
// largest a template allows — still gives every diagonal its own colour. A nine-box
// samples every other band and so keeps the five colours it always had.
const DEFAULT_CELL_COLORS = [
	...createCellColorVariants(palettes.vibrantPeach, '700', ['400', '600']),
	...createCellColorVariants(palettes.electricBlue, '600', ['400', '500', '600']),
	...createCellColorVariants(palettes.vividPink, '600', ['300', '400', '500', '600']),
];

const SUPPORTED_CELL_COLORS = [
	...createCellColorVariants(palettes.vividPink, '600', ['200', '400', '600']),
	...createCellColorVariants(palettes.vibrantPeach, '700', ['200', '400', '600']),
	...createCellColorVariants(palettes.electricBlue, '600', ['200', '400', '600']),
	...createCellColorVariants(palettes.freshGreen, '700', ['200', '400', '600']),
	...createCellColorVariants(palettes.coralRed, '600', ['200', '400', '600']),
];

const AVAILABLE_CELL_COLORS = new Map<number, TalentReviewCellColors>(
	SUPPORTED_CELL_COLORS.map((colors) => [encodeCellColor(colors.border), colors]),
);

export function isSupportedCellColor(color?: number): color is number {
	return color !== undefined && AVAILABLE_CELL_COLORS.has(color);
}

export function customCellColor(color?: number): string | undefined {
	return isSupportedCellColor(color) ? decodeCellColor(color) : undefined;
}

// Colours for the cell at (row, column) in a grid of rows × columns. Uses the cell's
// chosen palette colour when present, falling back to the default anti-diagonal colour.
export function cellColors(
	row: number,
	column: number,
	rows: number,
	columns: number,
	color?: number,
): TalentReviewCellColors {
	if (color !== undefined) {
		const custom = AVAILABLE_CELL_COLORS.get(color);
		if (custom) {
			return custom;
		}
	}

	const max = rows + columns - 2;
	const step =
		max <= 0
			? DEFAULT_CELL_COLORS.length - 1
			: Math.round(((row + column) / max) * (DEFAULT_CELL_COLORS.length - 1));

	return DEFAULT_CELL_COLORS[Math.min(DEFAULT_CELL_COLORS.length - 1, Math.max(0, step))];
}

// Numeric form of a cell's border colour, for the template's required `color` field.
export function cellColorValue(
	row: number,
	column: number,
	rows: number,
	columns: number,
	color?: number,
) {
	return encodeCellColor(cellColors(row, column, rows, columns, color).border);
}
